import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchCompanyProfile } from '@/api/companies';
import type { CompanyProfile } from '@/api/companies/types';
import type { DataPoint } from '@/api/slcp/types';
import { processesArray } from '@/lib/utils/facilityProcesses';

export interface CounterpartyProfile {
  /** ABOUT `official_name` datapoint. */
  officialName: string | null;
  /** ABOUT `internal_company_code` — the viewer's own code for this company. */
  identifier: string | null;
  /** No company-level datapoint exists — always `null` (renders "—"). */
  brands: string | null;
  /** Facility-process keys the company already uses (ABOUT `facility_processes`). */
  facilityProcesses: string[];
  /** `false` while loading or after a `profiles:read` 403 for an out-of-network
   *  counterparty — the fields above are unknown, not empty. */
  resolved: boolean;
}

const EMPTY: Omit<CounterpartyProfile, 'resolved'> = {
  officialName: null,
  identifier: null,
  brands: null,
  facilityProcesses: [],
};

/** Defensively string-coerce a datapoint value; blank/absent → null. */
function dpString(dp: DataPoint | undefined): string | null {
  const v = dp?.value;
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

/** ABOUT `facility_processes` → array of process keys. The datapoint value is
 *  canonically `{ id }[]`, but legacy/aggregated shapes can be a JSON or
 *  comma-joined string, so both are tolerated. */
function facilityProcessKeys(
  dp: DataPoint<{ id: string }[]> | undefined,
): string[] {
  const v = dp?.value;
  if (!v) return [];
  if (Array.isArray(v)) return v.map(x => x?.id).filter(Boolean);
  return processesArray(v as unknown as string);
}

/** The query is error-tolerant (any failure → `null` profile) because the
 *  backend `profiles:read` check can 403/404 for an out-of-network counterparty
 *  and a rejected loader would take down the whole order page. */
export function counterpartyProfileQueryOptions(
  companyId: string | null | undefined,
) {
  return queryOptions({
    queryKey: ['company-profile', 'about', companyId] as const,
    enabled: !!companyId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CompanyProfile | null> => {
      if (!companyId) return null;
      try {
        return await fetchCompanyProfile(companyId);
      } catch {
        return null;
      }
    },
  });
}

/** Read-only counterparty details sourced from the company ABOUT profile. */
export function useOrderHubCounterpartyProfile(
  companyId: string | null | undefined,
): CounterpartyProfile {
  const { data, isPending } = useQuery(
    counterpartyProfileQueryOptions(companyId),
  );

  if (!companyId) return { ...EMPTY, resolved: true };
  if (isPending || !data) return { ...EMPTY, resolved: false };

  return {
    officialName: dpString(data.data.official_name),
    identifier: dpString(data.data.internal_company_code),
    brands: null,
    facilityProcesses: facilityProcessKeys(data.data.facility_processes),
    resolved: true,
  };
}
